/**
 * signingErrors — GDN-3288 sign-time compliance failure client helpers.
 *
 * Signing, amending, or co-signing a note has the backend render the
 * compliance PDF. When that render fails for a CONTENT reason the backend
 * answers with the standard v2 `ClientErrorResponse` shape and error_code
 * "pdf_render_failed" (422); when the compliance service itself is
 * unreachable it answers "compliance_unavailable" (502). For both codes the
 * backend `message` IS the user-facing copy — it names the offending section
 * and the recovery step — so the client surfaces it verbatim instead of the
 * generic "Failed to … Please try again." fallback.
 */

import { candidateErrorBodies } from '@features/admin-v2/utils/api-error';
import type { BulkCosignItemResponse } from '@store/generated/notes';

/** Error codes whose backend `message` is written to be shown to the user. */
export const USER_FACING_SIGNING_ERROR_CODES = [
  'pdf_render_failed',
  'compliance_unavailable',
] as const;

export type UserFacingSigningErrorCode = (typeof USER_FACING_SIGNING_ERROR_CODES)[number];

/**
 * Snackbar options for these messages: they carry an instruction, so hold
 * them well past the app default (3s) — matches the repo's other
 * instruction-bearing error toasts.
 */
export const SIGNING_ERROR_SNACKBAR_OPTIONS = {
  variant: 'error',
  autoHideDuration: 10000,
  preventDuplicate: true,
} as const;

export const isUserFacingSigningErrorCode = (
  code: unknown,
): code is UserFacingSigningErrorCode =>
  typeof code === 'string' &&
  (USER_FACING_SIGNING_ERROR_CODES as readonly string[]).includes(code);

/**
 * The backend's message for a `pdf_render_failed` / `compliance_unavailable`
 * body, or null when the body is anything else (or carries no message).
 * Shared by the single-note mutation error path and the bulk per-item path.
 */
const getUserFacingMessage = (body: {
  error_code?: unknown;
  message?: unknown;
}): string | null => {
  if (!isUserFacingSigningErrorCode(body.error_code)) return null;
  return typeof body.message === 'string' && body.message.trim() ? body.message : null;
};

/**
 * The backend body of an RTK Query rejection. `auth0BaseQuery` re-wraps every
 * failure as `{ status, data: { status, message, data: <backend body> } }`, so
 * in the running app the body is at `error.data.data`; hand-built mocks pass
 * the flat `error.data`. `candidateErrorBodies` yields both depths; prefer the
 * one carrying an `error_code` — the wrapper's `message` is synthesized by the
 * base query, the coded body's is the backend's own.
 */
const getErrorBody = (error: unknown): { error_code?: unknown; message?: unknown } | null => {
  const bodies = candidateErrorBodies(error);
  return bodies.find(body => typeof body.error_code === 'string') ?? bodies[0] ?? null;
};

/**
 * The backend's stable `error_code` from a mutation rejection, for analytics
 * only — a bare code (e.g. "pdf_render_failed"), never the message, which
 * names note sections. Null when the rejection carries no string code.
 */
export const getSigningErrorCode = (error: unknown): string | null => {
  const code = getErrorBody(error)?.error_code;
  return typeof code === 'string' && code ? code : null;
};

/**
 * Recognize a sign / amend / co-sign mutation rejection (RTK Query's
 * `{ status, data }`) whose body is one of the user-facing codes, and return
 * the backend message to show. Null for every other error so callers keep
 * their generic fallback. Check the future-date-of-service 409 first — that
 * one opens a dialog rather than a toast.
 */
export const getSigningErrorMessage = (error: unknown): string | null => {
  const body = getErrorBody(error);
  return body ? getUserFacingMessage(body) : null;
};

/**
 * Bulk co-sign variant: the per-note result item carries `error_code` and
 * `message` directly (no HTTP envelope). Null unless the item failed with one
 * of the user-facing codes.
 */
export const getBulkCosignItemErrorMessage = (
  item: Pick<BulkCosignItemResponse, 'success' | 'error_code' | 'message'>,
): string | null => (item.success ? null : getUserFacingMessage(item));
